import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  assignPermissionsRequest,
  createRoleRequest,
  deleteRoleRequest,
  extractList,
  getPermissions,
  getRolePermissions,
  getRoles,
  updateRoleRequest,
} from "../../../services/api/rolesPermissions/rolesPermissionsApi";

export const fetchRoles = createAsyncThunk(
  "rolesPermissions/fetchRoles",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRoles();
      return extractList(response.data);
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to load roles",
      );
    }
  },
);

export const fetchPermissions = createAsyncThunk(
  "rolesPermissions/fetchPermissions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getPermissions();
      return extractList(response.data);
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to load permissions",
      );
    }
  },
);

export const fetchRolePermissions = createAsyncThunk(
  "rolesPermissions/fetchRolePermissions",
  async (roleId, { rejectWithValue }) => {
    try {
      const response = await getRolePermissions(roleId);
      const items = extractList(response.data);
      return items.map((item) => Number(item.id));
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to load role permissions",
      );
    }
  },
);

export const createRole = createAsyncThunk(
  "rolesPermissions/createRole",
  async (roleData, { rejectWithValue }) => {
    try {
      const response = await createRoleRequest(roleData);
      return response?.data?.data || response?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to create role",
      );
    }
  },
);

export const updateRole = createAsyncThunk(
  "rolesPermissions/updateRole",
  async ({ roleId, roleData }, { rejectWithValue }) => {
    try {
      const response = await updateRoleRequest(roleId, roleData);
      return response?.data?.data || response?.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to update role",
      );
    }
  },
);

export const removeRole = createAsyncThunk(
  "rolesPermissions/removeRole",
  async (roleId, { rejectWithValue }) => {
    try {
      await deleteRoleRequest(roleId);
      return roleId;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to delete role",
      );
    }
  },
);

export const assignPermissions = createAsyncThunk(
  "rolesPermissions/assignPermissions",
  async ({ roleId, permissionIds }, { rejectWithValue }) => {
    try {
      await assignPermissionsRequest(roleId, permissionIds);
      return permissionIds;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to save permissions",
      );
    }
  },
);

const initialState = {
  roles: [],
  permissions: [],
  rolePermissionIds: [],
  loading: false,
  error: null,
};

const rolesPermissionsSlice = createSlice({
  name: "rolesPermissions",
  initialState,
  reducers: {
    clearRolePermissionIds: (state) => {
      state.rolePermissionIds = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRoles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.loading = false;
        state.roles = action.payload;
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchPermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        state.loading = false;
        state.permissions = action.payload;
      })
      .addCase(fetchPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchRolePermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRolePermissions.fulfilled, (state, action) => {
        state.loading = false;
        state.rolePermissionIds = action.payload;
      })
      .addCase(fetchRolePermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRole.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRole.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(removeRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeRole.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(removeRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(assignPermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(assignPermissions.fulfilled, (state, action) => {
        state.loading = false;
        state.rolePermissionIds = action.payload;
      })
      .addCase(assignPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearRolePermissionIds } = rolesPermissionsSlice.actions;

export default rolesPermissionsSlice.reducer;
